/**
 * Owner: Masa Bayu
 * File: elevation-system.ts
 * Date:Time: 2026-09-13 21:26
 * Description: 22D QXElevationSystem — hierarchy levels (not CSS shadows).
 */
import { QX_DESIGN_SCALE_UNIT } from './spacing-system';
export const QX_ELEVATION_LEVELS = ['none', 'low', 'medium', 'high', 'maximum'];
export const QX_ELEVATION_SYSTEM_MODEL_VERSION = '1.0.0';
export const QX_ELEVATION_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'boxShadow',
    'shadow',
    'opacity',
    'blur',
    'renderer',
    'layout',
    'qxw',
    'qxp',
];
function assertNoForbidden(record, label) {
    for (const key of QX_ELEVATION_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
export function isQXElevationLevel(value) {
    return typeof value === 'string' && QX_ELEVATION_LEVELS.includes(value);
}
export function createQXElevationToken(level, value) {
    if (!isQXElevationLevel(level)) {
        throw new Error(`Invalid elevation level: ${String(level)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error('Elevation value must be a finite number >= 0');
    }
    return Object.freeze({
        level,
        value: value === 0 ? 0 : value,
        unit: QX_DESIGN_SCALE_UNIT,
    });
}
export function createQXElevationSystem(options) {
    assertNoForbidden({ ...options }, 'Elevation system');
    const list = options.elevations.map((token) => {
        assertNoForbidden({ ...token }, 'Elevation token');
        return createQXElevationToken(token.level, token.value);
    });
    const seen = new Set();
    for (const item of list) {
        if (seen.has(item.level)) {
            throw new Error(`Duplicate elevation level: ${item.level}`);
        }
        seen.add(item.level);
    }
    return Object.freeze({
        modelVersion: QX_ELEVATION_SYSTEM_MODEL_VERSION,
        elevations: Object.freeze(list),
    });
}
export function isQXElevationSystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return (record.modelVersion === QX_ELEVATION_SYSTEM_MODEL_VERSION && Array.isArray(record.elevations));
}
export function validateQXElevationSystem(value) {
    if (!isQXElevationSystem(value)) {
        throw new Error('Invalid elevation system');
    }
    createQXElevationSystem(value);
}
export function resolveQXElevationLevel(system, level) {
    if (!isQXElevationLevel(level)) {
        throw new Error(`Invalid elevation level: ${String(level)}`);
    }
    const token = system.elevations.find((item) => item.level === level);
    if (!token) {
        throw new Error(`Elevation system does not define level "${level}"`);
    }
    return token.value;
}
/** Immutable upsert of an elevation level — returns a new frozen system. */
export function withQXElevationLevel(system, level, value) {
    const next = system.elevations.filter((item) => item.level !== level);
    return createQXElevationSystem({
        elevations: [...next, createQXElevationToken(level, value)],
    });
}
//# sourceMappingURL=elevation-system.js.map